import { DOCUMENT } from '@angular/common';
import { Directive, DoCheck, ElementRef, HostBinding, HostListener, Inject, Input, Renderer2, Self } from '@angular/core';
import { NgControl, Validators } from '@angular/forms';
import { ValidationMessageService } from '../../services/validation-message/validation-message.service';
// Selector of this directive is as same as angular's built in form directive.
// This will minimize work on our side since we have to append formContolName
// to every form control in order for it to become part of angular's reactive
// form framework. Because this directive has exactly the same name it will also
// be added to that control - two birds with one stone basically.
// This directive will do some selective DOM manipulation to vary s_req CSS based
// on presenence of Validator.required and will also add angular's invalid-feedback
// DIV so we can add validation messages there
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector -- this is overriding NG's formControlName directive so it has to stay like this
  selector: '[formControlName]'
})
export class FormControlNameDirective implements DoCheck {
  private _cachedPlaceHolder = '';
  @Input() formControlName: string;

  private _clearErrorsIfOnSubmitMode() {
    if (this._ngControl.control.updateOn == 'submit' && this._ngControl.errors) {
      this._ngControl.control.setErrors(null);
    }
  }
  removeErrorMessage(element: any) {
    if (element == null) {
      return;
    }
    const invalidFeedback = element.querySelectorAll(':scope > .invalid-feedback');
    if (invalidFeedback && invalidFeedback.length > 0) {
      element.removeChild(invalidFeedback[0]);
    }
  }
  addErrorMessage(element: any, errors, id, dataTestId) {
    if (element == null) {
      return;
    }

    const elements = element.querySelectorAll(':scope > .invalid-feedback');
    let child = null;
    // NOTE: This kind of stuff is not to be used too often. Direct DOM
    // manipulation is best left to directives only (such as this one)
    // and even then used very sparingly. In this case it makes sense as we
    // want to add bootstraps invalid-feedback DIV dynamically rather than
    // ask Greg to add it to every control or have to do it ourselves.

    if (!elements || elements.length == 0) {
      child = document.createElement('div');
      if (id) {
        child.id = id + '-safari-validation-error';
      }
      if (dataTestId) {
        child.setAttribute('data-test-id', dataTestId + '-safari-validation-error');
      }
      child.style['display'] = 'block';
      child.style['white-space'] = 'pre-wrap';
      child.style['font-size'] = '0.9rem';
      child.style['font-weight'] = '500';
      child.style['text-transform'] = 'none';
      child.style['word-wrap'] = 'break-word';
      child.className = 'invalid-feedback s_error';
      this._renderer.appendChild(element, child);
    } else {
      child = elements[0];
    }
    // Clear all messages
    while (child.firstChild) {
      child.removeChild(child.firstChild);
    }
    const errorMessages = this._validationMessageService.getErrorMessages(errors);
    if (errorMessages && errorMessages.length) {
      // The code below would allow adding multiple messages with line breaks, if needed
      // let fullErrorMessage = '';
      // errorMessages.forEach(element => {
      //   fullErrorMessage += element;
      //   fullErrorMessage += '\r\n';
      // });
      // var newContent = document.createTextNode(fullErrorMessage);

      // However, we'll just display the first message
      const newContent = document.createTextNode(errorMessages[0]);

      child.appendChild(newContent);
    }
  }
  private isFormGroup(element) {
    return element.classList.contains('form-group');
  }
  ngDoCheck(): void {
    // Checks for presence of require validator and if present adds s_req class.
    // DO NOT bother trying to do this for any other kind of validator
    // (say, hyopthetically if we had a CSS class s_SpecialReq that we wanted to render if Validator.SpecialReq is present)
    // It won't work - validators in NG, other than "required" validator, are pure functions and
    // a) NG does not expose them
    // b) even if it did we wouldn't know what they are as they end up being compiled, minified functions

    let element = this._el.nativeElement;

    if (!element) {
      return;
    }

    // For some reason ngControl.control is NULL in the constructor but it's
    // available in ngDoCheck so we'll just assign that here.
    // Basically what we want is to be able to directly access the native element
    // that this formControl is tied to (and in their infinite wisdom NG team decided
    // not to provide that by default. Thankfully we already have this directive so we
    // can take advantage of it)

    // PS We used to have a check here to see if _nativeElement is already assigned and if not
    // we wouldn't try to assign it. This check by itself wouldn't really help anything with performance
    // as we're not retrieving anything special, just a simple assignment of what we already have.
    // However, it was causing a systemwide bug where if a form's control was taken out via ngIf or hidden
    // and then reappear again it would lose ability to scroll to it in case of an error.
    // That's because the original element was already assigned, but once it was ngIfed out of the DOM
    // it's not valid anymore. So we're removing that NULL check and just assigning it every time.
    // This issue was most noticable in serve component where when you'd switch to a different form, it would
    // stop scrolling to errors. But this could be probably seen in other places throughout the system.

    this._ngControl.control['_nativeElement'] = element;

    // NG-MULTISELECT HACK
    // There seems to be a bug in ngMultiSelect that doesn't respond to reactive form's disabled
    // attribute, like other controls do. So we'll just force it here...
    if (element.tagName?.toUpperCase() == 'NG-MULTISELECT-DROPDOWN') {
      if (this._ngControl.control.root['_readOnly']) {
        element.setAttribute('disabled', 'disabled');
      }
    }

    // ng-multiselect-dropdown
    // In some cases we want to override where the validation message will be added
    const customValidationMessageContainerId = element.getAttribute('data-validator-message-container-id');
    // In some cases we want to override where s_req symbol will be added
    const customValidationSReqContainerId = element.getAttribute('data-validator-s-req-container-id') || element.getAttribute('data-validator-s-soft-req-container-id');
    const parentGroupValidationContainer = element.getAttribute('data-validator-s-req-parent-group-id');

    // In some cases we don't want to automatically calculate s_req
    const supressSReq = element.classList.contains('no_auto_s_req') || this.readOnly;
    // Get the closest containing form-group. In 99% of the cases this is where we want to
    // add validation messages to
    while (element && !this.isFormGroup(element)) {
      // If direct parent is not form-group (sometimes controls are wrapped in input-group or something else possibly)
      // then loop until we hit form-group. s_req needs to be on form-group for proper formatting
      element = element.parentElement;
      if (element == null) {
        // if we went all the way up and didn't find form-group then we'll assign this to the first parent.
        // It probably won't look pretty (s_req will probably have some bottom offset) but at least it won't blow up
        element = this._el.nativeElement.parentElement;
        break;
      }
    }

    const validationMessageElement = customValidationMessageContainerId ? this._document.getElementById(customValidationMessageContainerId) : element;

    if (this._ngControl.errors) {
      if (this._ngControl.touched) {
        this.addErrorMessage(validationMessageElement, this._ngControl.errors, this._el.nativeElement.id, this._el.nativeElement.getAttribute('data-test-id'));
      }
    } else {
      this.removeErrorMessage(validationMessageElement);
    }

    if (this._ngControl.control && !supressSReq) {
      const sReqElement = customValidationSReqContainerId
        ? this._document.getElementById(customValidationSReqContainerId)
        : parentGroupValidationContainer
          ? this._document.getElementById(parentGroupValidationContainer)
          : element;

      if (this._ngControl.control.hasValidator(Validators.required) || this._ngControl.control['__safariRequiredControls'] != null) {
        if (sReqElement) {
          const spacingPrefix = parentGroupValidationContainer ? '&nbsp;&nbsp;' : '';
          this._renderer.addClass(sReqElement, 's_req');
          if (this._ngControl.control['__safariRequiredControls']?.length > 0 || parentGroupValidationContainer) {
            sReqElement.innerHTML = spacingPrefix + 'SELECT ONE OR MORE';
          } else if (this._ngControl.control['__safariRequiredControls']?.length === 0) {
            sReqElement.innerHTML = spacingPrefix + 'ALL REQUIRED';
          }
        }
      } else {
        if (sReqElement) {
          this._renderer.removeClass(sReqElement, 's_req');
          if (sReqElement.hasAttributes() && sReqElement.attributes['data-s-req-checkbox']) {
            sReqElement.innerText = '';
          }
        }
      }
    }
    if (this.readOnly) {
      const sReqElement = customValidationSReqContainerId ? this._document.getElementById(customValidationSReqContainerId) : element;
      if (sReqElement) {
        this._renderer.removeClass(sReqElement, 's_sft');
      }

      this._cachedPlaceHolder = this._el.nativeElement.getAttribute('placeholder');

      // In general we want to hide placeholder for disabled controls but there are some weird exceptions
      // like the typeahead control which actually uses the placeholder to display the initial value.
      // Controls that want their placeholder to remain intact can use data-safari-keep-placeholder attribute
      if (this._cachedPlaceHolder && !this._el.nativeElement.getAttribute('data-safari-keep-placeholder')) {
        this._renderer.setAttribute(this._el.nativeElement, 'placeholder', '');
      }
    } else if (this._cachedPlaceHolder) {
      this._renderer.setAttribute(this._el.nativeElement, 'placeholder', this._cachedPlaceHolder);
    }
  }
  // By default NG will not refresh validators if form is set to update in onSubmit,
  // but that looks a bit weird. The code below simply resets any errors the moment you start
  // changing the value and clears them out.
  @HostListener('keyup')
  onKeyUp() {
    this._clearErrorsIfOnSubmitMode();
  }
  @HostListener('change')
  onChange() {
    this._clearErrorsIfOnSubmitMode();
  }

  @HostBinding('class.is-invalid')
  get invalid() {
    if (this._ngControl.control.updateOn == 'submit' && this._ngControl.control.root['suppressValidationMessages']) {
      return false;
    }
    const isInvalid = this._ngControl.touched && this._ngControl.errors;
    return isInvalid;
  }
  @HostBinding('class.read-only')
  get readOnly() {
    return this._ngControl.control.root['_readOnly'] || this._ngControl.control.disabled;
  }

  constructor(
    @Self() private _ngControl: NgControl,
    private _el: ElementRef,
    private _renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document,
    private _validationMessageService: ValidationMessageService
  ) {}
}
